// pages/Quotation.jsx - Formulaire public complet et corrigé
import { useState } from 'react';
import { quotesAPI } from '../api/quotes';
import { Send, FileText, Calculator, Check, Building, User, Mail, Phone, MessageSquare, Euro, Calendar } from 'lucide-react';
import Loader from '../components/common/Loader';
import '../styles/quotation.css';

export default function Quotation() {
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '', // Utiliser clientEmail au lieu de email
    phone: '',
    company: '',
    projectType: '',
    projectDescription: '', // Utiliser projectDescription au lieu de description
    budgetRange: '',
    deadline: '',
    features: [],
    additionalNotes: ''
  });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const projectTypes = [
    'Site Web',
    'Application Mobile', 
    'E-commerce',
    'Refonte',
    'Maintenance',
    'Autre'
  ];

  const budgetRanges = [
    'Moins de 1 000€',
    '1 000€ - 3 000€',
    '3 000€ - 5 000€',
    '5 000€ - 10 000€',
    '10 000€ - 20 000€',
    'Plus de 20 000€',
    'À définir'
  ];

  const featuresList = [
    'Design responsive',
    'Blog intégré',
    'Formulaire de contact',
    'Intégration réseaux sociaux',
    'Site multilingue',
    'Espace client/membre',
    'Système de paiement en ligne',
    'Catalogue produits',
    'Gestion des stocks',
    'Newsletter automatisée',
    'Optimisation SEO',
    'Analytics et statistiques',
    'Chat en direct',
    'Réservation en ligne',
    'Base de données',
    'API intégration'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Préparer les données selon le modèle Quote
      const quoteData = {
        // Champs requis par le modèle
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        phone: formData.phone,
        company: formData.company || '',
        
        // Informations du projet
        projectType: formData.projectType,
        projectDescription: formData.projectDescription,
        budgetRange: formData.budgetRange || 'À définir',
        deadline: formData.deadline || null,
        features: formData.features || [],
        additionalNotes: formData.additionalNotes || '',
        
        // Le numéro sera généré automatiquement par le modèle
        // Les champs financiers restent à 0 (seront remplis par l'admin)
        subtotal: 0,
        tax: 0,
        total: 0,
        items: [],
        
        // Statut initial
        status: 'pending',
        
        // Source de la demande
        source: 'Formulaire site web'
        // ipAddress sera récupéré automatiquement par le backend
      };

      console.log('Données envoyées:', quoteData);
      
      // Utiliser la route publique pour les visiteurs
      const response = await quotesAPI.createPublic(quoteData);
      console.log('Réponse du serveur:', response.data);
      
      // Marquer comme soumis
      setSubmitted(true);
      
    } catch (error) {
      console.error('Erreur complète:', error);
      
      // Messages d'erreur spécifiques
      let errorMessage = 'Erreur lors de la demande de devis. Veuillez réessayer.';
      
      if (error.response?.data?.errors) {
        // Erreurs de validation Mongoose
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors).map(err => 
          err.properties?.message || err.message || err
        ).join(', ');
      } else if (error.response?.data?.message) {
        // Message d'erreur du serveur
        errorMessage = error.response.data.message;
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Erreur de connexion au serveur. Veuillez vérifier votre connexion.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step < 3) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  // Validation des étapes
  const validateStep1 = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9+\s()-]{10,}$/;
    
    return (
      formData.clientName.trim().length >= 2 &&
      emailRegex.test(formData.clientEmail) &&
      phoneRegex.test(formData.phone.replace(/\s/g, ''))
    );
  };

  const validateStep2 = () => {
    return (
      formData.projectType &&
      formData.projectDescription.trim().length >= 20
    );
  };

  if (loading) return <Loader />;

  if (submitted) {
    return (
      <div className="quotation-page">
        {/* En-tête */}
        <section className="quotation-hero">
          <div className="container">
            <div className="hero-content">
              <div className="hero-badge">
                <Check size={24} />
                <span>Demande envoyée</span>
              </div>
              <h1>Merci pour votre demande !</h1>
              <p>Votre projet est entre de bonnes mains</p>
            </div>
          </div>
        </section>

        {/* Message de succès */}
        <section className="success-section">
          <div className="container">
            <div className="success-card">
              <div className="success-icon">
                <Check size={64} color="#10b981" />
              </div>
              
              <h2>Demande de devis confirmée</h2>
              
              <div className="success-details">
                <div className="detail-item">
                  <strong>Numéro de référence :</strong>
                  <span>En attente de génération</span>
                </div>
                <div className="detail-item">
                  <strong>Email de confirmation :</strong>
                  <span>{formData.clientEmail}</span>
                </div>
                <div className="detail-item">
                  <strong>Projet :</strong>
                  <span>{formData.projectType}</span>
                </div>
              </div>
              
              <p className="success-message">
                Nous analysons votre projet et vous enverrons un devis détaillé 
                dans les <strong>24 heures ouvrées</strong>.
              </p>
              
              <div className="next-steps">
                <h4>Prochaines étapes :</h4>
                <ul>
                  <li>Analyse de votre projet par notre équipe technique</li>
                  <li>Élaboration d'un devis personnalisé</li>
                  <li>Envoi du devis par email avec nos recommandations</li>
                  <li>Suivi par téléphone pour répondre à vos questions</li>
                </ul>
              </div>
              
              <div className="success-actions">
                <button 
                  onClick={() => window.location.href = '/'} 
                  className="btn-primary"
                >
                  Retour à l'accueil
                </button>
                <button 
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      clientName: '',
                      clientEmail: '',
                      phone: '',
                      company: '',
                      projectType: '',
                      projectDescription: '',
                      budgetRange: '',
                      deadline: '',
                      features: [],
                      additionalNotes: ''
                    });
                    setStep(1);
                  }} 
                  className="btn-outline"
                >
                  Nouvelle demande
                </button>
              </div>
              
              <div className="contact-info">
                <p>
                  <strong>Questions ?</strong> Contactez-nous au{' '}
                  <a href="tel:+33123456789">01 23 45 67 89</a> ou par email à{' '}
                  <a href="mailto:contact@digitalpro.com">contact@digitalpro.com</a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="quotation-page">
      {/* En-tête */}
      <section className="quotation-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <Calculator size={24} />
              <span>Devis gratuit</span>
            </div>
            <h1>Demandez votre devis personnalisé</h1>
            <p>
              Remplissez ce formulaire en 3 étapes simples et obtenez un devis 
              détaillé adapté à votre projet numérique.
            </p>
            <div className="hero-stats">
              <div className="stat">
                <strong>24h</strong>
                <span>Réponse garantie</span>
              </div>
              <div className="stat">
                <strong>100%</strong>
                <span>Personnalisé</span>
              </div>
              <div className="stat">
                <strong>Gratuit</strong>
                <span>Sans engagement</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stepper */}
      <section className="stepper-section">
        <div className="container">
          <div className="quotation-stepper">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>
              <div className="step-circle">
                <span>1</span>
              </div>
              <div className="step-info">
                <div className="step-title">Informations</div>
                <div className="step-desc">Vos coordonnées</div>
              </div>
            </div>
            
            <div className={`step ${step >= 2 ? 'active' : ''}`}>
              <div className="step-circle">
                <span>2</span>
              </div>
              <div className="step-info">
                <div className="step-title">Projet</div>
                <div className="step-desc">Détails du projet</div>
              </div>
            </div>
            
            <div className={`step ${step >= 3 ? 'active' : ''}`}>
              <div className="step-circle">
                <span>3</span>
              </div>
              <div className="step-info">
                <div className="step-title">Fonctionnalités</div>
                <div className="step-desc">Besoins spécifiques</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="form-section">
        <div className="container">
          {error && (
            <div className="error-alert">
              <div className="error-icon">⚠️</div>
              <div className="error-content">
                <strong>Une erreur est survenue :</strong>
                <p>{error}</p>
                <button 
                  onClick={() => setError('')}
                  className="error-close"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="quotation-form">
            {/* Étape 1 : Informations personnelles */}
            {step === 1 && (
              <div className="form-step active">
                <div className="step-header">
                  <div className="step-icon">
                    <User size={32} />
                  </div>
                  <div>
                    <h2>Vos informations</h2>
                    <p className="step-description">
                      Renseignez vos coordonnées pour que nous puissions vous recontacter
                    </p>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="clientName">
                      Nom complet <span className="required">*</span>
                    </label>
                    <input
                      id="clientName"
                      type="text"
                      value={formData.clientName}
                      onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                      required
                      placeholder="Jean Dupont"
                      minLength={2}
                    />
                    <div className="form-help">
                      Votre nom complet pour vous adresser correctement
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Entreprise (optionnel)</label>
                    <div className="input-with-icon">
                      <Building size={20} />
                      <input
                        id="company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="DigitalPro SARL"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="clientEmail">
                      Adresse email <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <Mail size={20} />
                      <input
                        id="clientEmail"
                        type="email"
                        value={formData.clientEmail}
                        onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                        required
                        placeholder="jean.dupont@exemple.com"
                      />
                    </div>
                    <div className="form-help">
                      Nous vous enverrons le devis à cette adresse
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      Numéro de téléphone <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <Phone size={20} />
                      <input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        placeholder="+33 1 23 45 67 89"
                        pattern="[0-9+\s()-]{10,}"
                      />
                    </div>
                    <div className="form-help">
                      Pour vous contacter rapidement si besoin
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <div className="progress-info">
                    Étape {step} sur 3
                  </div>
                  <div className="action-buttons">
                    <button
                      type="button"
                      onClick={nextStep}
                      className="btn-primary"
                      disabled={!validateStep1()}
                    >
                      Continuer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Étape 2 : Détails du projet */}
            {step === 2 && (
              <div className="form-step active">
                <div className="step-header">
                  <div className="step-icon">
                    <FileText size={32} />
                  </div>
                  <div>
                    <h2>Détails de votre projet</h2>
                    <p className="step-description">
                      Décrivez-nous votre projet pour que nous puissions vous proposer la meilleure solution
                    </p>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="projectType">
                      Type de projet <span className="required">*</span>
                    </label>
                    <select
                      id="projectType"
                      value={formData.projectType}
                      onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                      required
                    >
                      <option value="">Choisissez un type de projet...</option>
                      {projectTypes.map((type, index) => (
                        <option key={index} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <div className="form-help">
                      Sélectionnez la catégorie qui correspond le mieux à votre projet
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="budgetRange">Budget estimé</label>
                    <select
                      id="budgetRange"
                      value={formData.budgetRange}
                      onChange={(e) => setFormData({ ...formData, budgetRange: e.target.value })}
                    >
                      <option value="">Indiquez votre budget...</option>
                      {budgetRanges.map((range, index) => (
                        <option key={index} value={range}>
                          {range}
                        </option>
                      ))}
                    </select>
                    <div className="form-help">
                      Nous adapterons notre proposition à votre budget
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="deadline">Date de livraison souhaitée</label>
                    <div className="input-with-icon">
                      <Calendar size={20} />
                      <input
                        id="deadline"
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="form-help">
                      Date approximative pour planifier le projet
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="projectDescription">
                      Description du projet <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <MessageSquare size={20} />
                      <textarea
                        id="projectDescription"
                        value={formData.projectDescription}
                        onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
                        required
                        rows={6}
                        placeholder="Décrivez votre projet en détail : objectifs, public cible, fonctionnalités souhaitées, contraintes particulières..."
                        minLength={20}
                      />
                    </div>
                    <div className="form-help">
                      Plus votre description est précise, plus notre devis sera adapté (minimum 20 caractères)
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <div className="progress-info">
                    Étape {step} sur 3
                  </div>
                  <div className="action-buttons">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="btn-secondary"
                    >
                      Retour
                    </button>
                    <button
                      type="button"
                      onClick={nextStep}
                      className="btn-primary"
                      disabled={!validateStep2()}
                    >
                      Continuer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Étape 3 : Fonctionnalités */}
            {step === 3 && (
              <div className="form-step active">
                <div className="step-header">
                  <div className="step-icon">
                    <Euro size={32} />
                  </div>
                  <div>
                    <h2>Fonctionnalités souhaitées</h2>
                    <p className="step-description">
                      Sélectionnez les fonctionnalités dont vous avez besoin (optionnel)
                    </p>
                  </div>
                </div>

                <div className="features-section">
                  <div className="features-intro">
                    <p>
                      Cochez les fonctionnalités qui vous intéressent. Cette liste nous aide 
                      à mieux comprendre vos besoins et à établir un devis précis.
                    </p>
                  </div>

                  <div className="features-grid">
                    {featuresList.map((feature, index) => (
                      <div
                        key={index}
                        className={`feature-card ${formData.features.includes(feature) ? 'selected' : ''}`}
                        onClick={() => toggleFeature(feature)}
                      >
                        <div className="feature-checkbox">
                          {formData.features.includes(feature) ? (
                            <div className="checkbox-checked">✓</div>
                          ) : (
                            <div className="checkbox-empty"></div>
                          )}
                        </div>
                        <div className="feature-content">
                          <div className="feature-title">{feature}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="additionalNotes">Notes supplémentaires (optionnel)</label>
                    <textarea
                      id="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                      rows={4}
                      placeholder="Précisez ici toute autre information importante : exigences techniques, intégrations spécifiques, contraintes particulières..."
                    />
                    <div className="form-help">
                      Toute information supplémentaire qui pourrait nous aider
                    </div>
                  </div>

                  <div className="selected-features-summary">
                    {formData.features.length > 0 && (
                      <>
                        <h4>Fonctionnalités sélectionnées ({formData.features.length}) :</h4>
                        <div className="selected-tags">
                          {formData.features.map((feature, index) => (
                            <span key={index} className="feature-tag">
                              {feature}
                              <button
                                type="button"
                                onClick={() => toggleFeature(feature)}
                                className="tag-remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <div className="progress-info">
                    Étape {step} sur 3
                  </div>
                  <div className="action-buttons">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="btn-secondary"
                    >
                      Retour
                    </button>
                    <button
                      type="submit"
                      className="btn-primary submit-btn"
                      disabled={loading || !validateStep1() || !validateStep2()}
                    >
                      {loading ? (
                        <>
                          <div className="spinner"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Envoyer la demande
                        </>
                      )}
                    </button>
                  </div>
                  <div className="form-disclaimer">
                    <p>
                      En cliquant sur "Envoyer la demande", vous acceptez que nous traitions 
                      vos informations pour vous fournir un devis. Vos données sont protégées 
                      et ne seront jamais partagées avec des tiers.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>

          {/* Informations supplémentaires */}
          <div className="form-info">
            <div className="info-card">
              <h3>Pourquoi choisir DigitalPro ?</h3>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-icon">⚡</div>
                  <h4>Réponse rapide</h4>
                  <p>Devis détaillé sous 24h ouvrées maximum</p>
                </div>
                <div className="info-item">
                  <div className="info-icon">🎯</div>
                  <h4>100% personnalisé</h4>
                  <p>Adapté à vos besoins et contraintes budgétaires</p>
                </div>
                <div className="info-item">
                  <div className="info-icon">🔒</div>
                  <h4>Transparence totale</h4>
                  <p>Pas de frais cachés, devis détaillé ligne par ligne</p>
                </div>
                <div className="info-item">
                  <div className="info-icon">📞</div>
                  <h4>Accompagnement</h4>
                  <p>Consultation gratuite incluse avec un expert</p>
                </div>
              </div>
            </div>
            
            <div className="guarantee-card">
              <h3>Notre garantie</h3>
              <ul>
                <li>✅ Devis gratuit et sans engagement</li>
                <li>✅ Proposition adaptée à votre budget</li>
                <li>✅ Suivi personnalisé tout au long du projet</li>
                <li>✅ Support technique dédié</li>
                <li>✅ Livraison dans les délais convenus</li>
              </ul>
              <div className="contact-support">
                <p>
                  <strong>Besoin d'aide ?</strong> Appelez-nous au{' '}
                  <a href="tel:+33123456789">01 23 45 67 89</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}